/**
 * Pure data builders for the skeleton overlay.
 *
 * The overlay draws the same thing from three different sources depending on the mode
 * (a bound rig in ANIMATE/MAP, a placement in RIG, raw source bones in MAP). Keeping the
 * "which dots and which lines" decision here — as plain tuples, no three.js objects — makes it
 * testable without a WebGL context and keeps `skeletonLayer.ts` about rendering only.
 */

import { CANONICAL_BONE_IDS, CANONICAL_PARENT } from '../../shared';
import type { CanonicalBoneId, CanonicalRigPlacement, Vec3Tuple } from '../../shared';

export interface OverlayJoint {
  id: CanonicalBoneId;
  position: Vec3Tuple;
}

export interface OverlayLink {
  parent: CanonicalBoneId;
  child: CanonicalBoneId;
  from: Vec3Tuple;
  to: Vec3Tuple;
}

export interface SkeletonOverlayData {
  joints: OverlayJoint[];
  links: OverlayLink[];
}

/** Minimal slice of `BoundRig` the overlay needs — keeps tests free of a full bind. */
export interface OverlayRigSource {
  mappedBoneIds(): CanonicalBoneId[];
  worldPosition(id: CanonicalBoneId): Vec3Tuple;
}

const EMPTY: SkeletonOverlayData = { joints: [], links: [] };

/**
 * Build the links implied by the canonical hierarchy, skipping unmapped intermediates: a bone
 * whose canonical parent is absent is connected to its nearest *present* canonical ancestor, so
 * a rig without a mapped `Root` or `Spine` still draws as one connected skeleton instead of
 * falling apart into floating segments.
 */
function linksFor(positions: Map<CanonicalBoneId, Vec3Tuple>): OverlayLink[] {
  const links: OverlayLink[] = [];
  for (const id of CANONICAL_BONE_IDS) {
    const to = positions.get(id);
    if (!to) continue;

    let ancestor = CANONICAL_PARENT[id];
    while (ancestor && !positions.has(ancestor)) ancestor = CANONICAL_PARENT[ancestor];
    if (!ancestor) continue;

    const from = positions.get(ancestor);
    if (!from) continue;
    links.push({ parent: ancestor, child: id, from, to });
  }
  return links;
}

function build(positions: Map<CanonicalBoneId, Vec3Tuple>): SkeletonOverlayData {
  const joints: OverlayJoint[] = [];
  for (const id of CANONICAL_BONE_IDS) {
    const position = positions.get(id);
    if (position) joints.push({ id, position });
  }
  return { joints, links: linksFor(positions) };
}

/**
 * Overlay for a bound rig (ANIMATE / MAP): the live world position of every mapped canonical
 * bone. Read fresh every frame — these positions move with the pose.
 */
export function overlayFromRig(rig: OverlayRigSource | null | undefined): SkeletonOverlayData {
  if (!rig) return EMPTY;
  const positions = new Map<CanonicalBoneId, Vec3Tuple>();
  for (const id of rig.mappedBoneIds()) positions.set(id, rig.worldPosition(id));
  return build(positions);
}

/**
 * Overlay for RIG mode: the rest skeleton the user is placing. Unlike the rig source this
 * includes `Root`, because placement is a complete `Record` over every canonical bone.
 */
export function overlayFromPlacement(
  placement: CanonicalRigPlacement | null | undefined,
): SkeletonOverlayData {
  if (!placement) return EMPTY;
  const positions = new Map<CanonicalBoneId, Vec3Tuple>();
  for (const id of CANONICAL_BONE_IDS) {
    const p = placement.jointWorldPositions[id];
    if (p) positions.set(id, [p[0], p[1], p[2]]);
  }
  return build(positions);
}

/** Overlay picked by mode — the single decision the layer makes each frame. */
export function overlayForMode(args: {
  mode: string;
  rig: OverlayRigSource | null | undefined;
  placement: CanonicalRigPlacement | null | undefined;
}): SkeletonOverlayData {
  if (args.mode === 'rig') return overlayFromPlacement(args.placement);
  if (args.mode === 'animate' || args.mode === 'map') return overlayFromRig(args.rig);
  return EMPTY;
}
